import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Text,
  TabNavigation,
  Icon,
  IconButton,
  Badge,
  ListGroup,
  ListGroupItem,
  WithTooltip,
  Dialogue,
  Checkbox
} from '@albert-io/atomic';
import {pluralize} from 'lib/stringUtils';
import {Link as RouterLink} from 'react-router';
import {getModelForResourceType} from 'resources/modelRegistry';
import {refreshTeacherList} from 'client/SchoolAdministration/School/ManageStaff/Staff/UpdateTeachers/updateTeachers.utils';
import ConfirmActionModal from 'client/components/ConfirmActionModal/ConfirmActionModal.react';

import 'client/SchoolAdministration/School/ManageStaff/Staff/UpdateTeachers/update-teachers.scss';

const RefreshTeacherList = ({
  activeTab,
  emailMatches,
  missingEmails,
  newEmails,
  pendingStaff,
  params,
  schoolTeachers,
  setActiveTab,
  rejectedTeachers
}) => {
  const totalEmails = [...newEmails, ...emailMatches];

  return (
    <Card paddingLevel={3} shadow='far' className='update-teachers__email-table u-mar-t_6'>
      <TabNavigation paddingLevel={0} fillWidth>
        <TabNavigation.Tab
          isActive={activeTab === 'teacher-list'}
          onClick={() => setActiveTab('teacher-list')}
        >
          Teacher List ({totalEmails.length})
          <Icon icon='check' className='u-mar-l_2' />
        </TabNavigation.Tab>
        <TabNavigation.Tab
          isActive={activeTab === 'to-be-removed'}
          onClick={() => setActiveTab('to-be-removed')}
        >
          To Be Removed ({missingEmails.length})
          <Icon icon='ban' className='u-mar-l_2' />
        </TabNavigation.Tab>
      </TabNavigation>
      {activeTab === 'teacher-list' ? (
        <TeacherListTab
          newEmails={newEmails}
          missingEmails={missingEmails}
          schoolTeachers={schoolTeachers}
          pendingStaff={pendingStaff}
          params={params}
          emailMatches={emailMatches}
          rejectedTeachers={rejectedTeachers}
        />
      ) : (
        <ToBeRemovedTab
          newEmails={newEmails}
          missingEmails={missingEmails}
          schoolTeachers={schoolTeachers}
          pendingStaff={pendingStaff}
          params={params}
          emailMatches={emailMatches}
          rejectedTeachers={rejectedTeachers}
        />
      )}
    </Card>
  );
};

RefreshTeacherList.propTypes = {
  emailMatches: PropTypes.array,
  newEmails: PropTypes.array,
  missingEmails: PropTypes.array,
  activeTab: PropTypes.string,
  setActiveTab: PropTypes.func,
  schoolTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  pendingStaff: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  rejectedTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  params: PropTypes.object
};

const TeacherListTab = ({
  newEmails,
  missingEmails,
  schoolTeachers,
  pendingStaff,
  params,
  emailMatches,
  rejectedTeachers
}) => {
  const totalEmails = [...newEmails, ...emailMatches];
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      {totalEmails.length ? (
        <>
          <ListGroup className='u-mar-t_4'>
            {Object.values(totalEmails).map((email, i) => (
              <ListGroupItem key={email} paddingLevel={1}>
                {newEmails.includes(email) ? (
                  <>
                    <Text>{`${i + 1}. ${email}`}</Text>
                    <Badge size='small' color='emphasized' className='u-mar-l_2'>
                      New teacher - Invitation will be sent
                    </Badge>
                  </>
                ) : (
                  `${i + 1}. ${email}`
                )}
              </ListGroupItem>
            ))}
          </ListGroup>
          {!newEmails.length ? (
            <div className='u-mar-t_2'>
              {missingEmails.length ? (
                <>
                  <div className='u-text-align_right u-display_flex u-align-items_center u-mar-t_4'>
                    <Text
                      className='u-mar-r_2 u-mar-l_auto u-max-width_48ch u-display_flex u-align-items_center'
                      emphasis='low'
                      size='medium'
                    >
                      <Icon icon='info-circle' className='u-mar-r_1' />
                      Teachers will be approved automatically.
                      <WithTooltip
                        content='All teachers have already been invited or have accepted their invitations, so they will be approved automatically.'
                        display='flex'
                      >
                        <IconButton icon={['far', 'question-circle']} />
                      </WithTooltip>
                    </Text>
                    <Button
                      onClick={() => {
                        setShowModal(true);
                      }}
                    >
                      Invite &amp; Update Teacher List
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className='u-text-align_right u-display_flex u-align-items_center u-mar-t_4'>
                    <Text
                      className='u-mar-r_2 u-mar-l_auto u-max-width_48ch u-display_flex u-align-items_center'
                      emphasis='low'
                      size='medium'
                    >
                      <Icon icon='exclamation-triangle' className='u-mar-r_1' />
                      No changes will be made.
                      <WithTooltip
                        content='All teachers have already been invited or have accepted their invitations.'
                        display='flex'
                      >
                        <IconButton icon={['far', 'question-circle']} />
                      </WithTooltip>
                    </Text>
                    <Button disabled>Invite &amp; Update Teacher List</Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className='u-mar-t_2 u-text-align_right'>
              {missingEmails.length ? (
                <>
                  <div className='u-align-items_right u-mar-t_4'>
                    <Button
                      onClick={() => {
                        setShowModal(true);
                      }}
                    >
                      Invite &amp; Update Teacher List
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className='u-text-align_right'>
                    <Button
                      as={RouterLink}
                      to={`/school-admin/${params.schoolId}/manage-staff`}
                      onClick={() =>
                        refreshTeacherList({
                          newEmails,
                          schoolId: params.schoolId,
                          missingEmails,
                          schoolTeachers,
                          pendingStaff,
                          rejectedTeachers
                        })
                      }
                      className='u-mar-t_4'
                    >
                      Invite &amp; Update Teacher List
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <div className='u-text-align_right'>
          <Text
            as='div'
            size='xl'
            emphasis='disabled'
            className='u-display_flex u-flex-direction_column u-align-items_center u-mar-t_4 u-mar-b_2'
          >
            <Icon
              size='inherit'
              icon={['fal', 'exclamation-circle']}
              className='update-teachers__info-icon u-mar-r_2 u-mar-t_4 u-mar-b_1 u-color_slate-400 u-mar-lr_auto u-display_block'
            />
            <Text as='div' className='u-mar-b_4 u-mar-l_4 u-text-align_center u-color_slate-500'>
              No teachers to add. The emails you provided already belong to administrator accounts.
              Admins cannot be added or removed during this process.
            </Text>
          </Text>
          <Button disabled>Invite &amp; Update Teacher List</Button>
        </div>
      )}
      {showModal && (
        <WarningModal
          missingEmails={missingEmails}
          showModal={showModal}
          setShowModal={setShowModal}
          params={params}
          newEmails={newEmails}
          schoolTeachers={schoolTeachers}
          pendingStaff={pendingStaff}
          rejectedTeachers={rejectedTeachers}
        />
      )}
    </>
  );
};

TeacherListTab.propTypes = {
  emailMatches: PropTypes.array,
  newEmails: PropTypes.array,
  missingEmails: PropTypes.array,
  schoolTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  rejectedTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  pendingStaff: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  params: PropTypes.object
};

const ToBeRemovedTab = ({
  newEmails,
  missingEmails,
  schoolTeachers,
  pendingStaff,
  params,
  emailMatches,
  rejectedTeachers
}) => {
  const totalEmails = [...newEmails, ...emailMatches];
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      {totalEmails.length || missingEmails.length ? (
        <>
          {missingEmails.length ? (
            <>
              <Text as='div' className='u-pad-t_4 u-pad-l_4'>
                The following teachers will no longer have access to your school license and will be
                moved to the Rejected table.
              </Text>
              <ListGroup className='u-mar-t_4'>
                {Object.values(missingEmails).map((email, i) => (
                  <ListGroupItem key={email} paddingLevel={1}>
                    {newEmails.includes(email) ? (
                      <>
                        <Text>{`${i + 1}. ${email}`}</Text>
                        <Badge size='small' color='emphasized' className='u-mar-l_2'>
                          New teacher - Invitation will be sent
                        </Badge>
                      </>
                    ) : (
                      `${i + 1}. ${email}`
                    )}
                  </ListGroupItem>
                ))}
              </ListGroup>
              <div className='u-text-align_right'>
                <Button
                  onClick={() => {
                    setShowModal(true);
                  }}
                  className='u-mar-t_4'
                >
                  Invite &amp; Update Teacher List
                </Button>
              </div>
            </>
          ) : (
            <>
              <Text
                as='div'
                size='xl'
                emphasis='disabled'
                className='u-display_flex u-flex-direction_column u-align-items_center u-mar-t_4 u-mar-b_2'
              >
                <Icon
                  size='inherit'
                  icon={['fal', 'info-circle']}
                  className='update-teachers__info-icon u-mar-r_2 u-mar-t_4 u-mar-b_1 u-color_slate-400 u-mar-lr_auto u-display_block'
                />
                <Text
                  as='div'
                  className='u-mar-b_4 u-mar-l_4 u-text-align_center u-color_slate-500'
                >
                  No teachers will be removed.
                </Text>
              </Text>
              {!newEmails.length ? (
                <>
                  <div className='u-text-align_right u-display_flex u-align-items_center u-mar-t_4'>
                    <Text
                      className='u-mar-r_2 u-mar-l_auto u-max-width_48ch u-display_flex u-align-items_center'
                      emphasis='low'
                      size='medium'
                    >
                      <Icon icon='exclamation-triangle' className='u-mar-r_1' />
                      No changes will be made.
                      <WithTooltip
                        content='All teachers have already been invited or have accepted their invitations.'
                        display='flex'
                      >
                        <IconButton icon={['far', 'question-circle']} />
                      </WithTooltip>
                    </Text>
                    <Button disabled>Invite &amp; Update Teacher List</Button>
                  </div>
                </>
              ) : (
                <div className='u-text-align_right'>
                  <Button
                    as={RouterLink}
                    to={`/school-admin/${params.schoolId}/manage-staff`}
                    onClick={() =>
                      refreshTeacherList({
                        newEmails,
                        schoolId: params.schoolId,
                        missingEmails,
                        schoolTeachers,
                        pendingStaff,
                        rejectedTeachers
                      })
                    }
                    className='u-mar-t_4'
                  >
                    Invite &amp; Update Teacher List
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className='u-text-align_right'>
          <Text
            as='div'
            size='xl'
            emphasis='disabled'
            className='u-display_flex u-flex-direction_column u-align-items_center u-mar-t_4 u-mar-b_2'
          >
            <Icon
              size='inherit'
              icon={['fal', 'exclamation-circle']}
              className='update-teachers__info-icon u-mar-r_2 u-mar-t_4 u-mar-b_1 u-color_slate-400 u-mar-lr_auto u-display_block'
            />
            <Text as='div' className='u-mar-b_4 u-mar-l_4 u-text-align_center u-color_slate-500'>
              No teachers to add. The emails you provided already belong to administrator accounts.
              Admins cannot be added or removed during this process.
            </Text>
          </Text>
          <Button disabled>Invite &amp; Update Teacher List</Button>
        </div>
      )}
      {showModal && (
        <WarningModal
          missingEmails={missingEmails}
          showModal={showModal}
          setShowModal={setShowModal}
          params={params}
          newEmails={newEmails}
          schoolTeachers={schoolTeachers}
          pendingStaff={pendingStaff}
          rejectedTeachers={rejectedTeachers}
        />
      )}
    </>
  );
};

ToBeRemovedTab.propTypes = {
  newEmails: PropTypes.array,
  missingEmails: PropTypes.array,
  params: PropTypes.object,
  emailMatches: PropTypes.array,
  schoolTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  rejectedTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  pendingStaff: PropTypes.instanceOf(getModelForResourceType('school_v5'))
};

const WarningModal = ({
  missingEmails,
  setShowModal,
  params,
  newEmails,
  schoolTeachers,
  pendingStaff,
  rejectedTeachers
}) => {
  const [checked, setChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const onCheckboxChange = () => {
    setErrorMessage('');
    setChecked(!checked);
  };

  const onSubmit = () => {
    if (checked) {
      refreshTeacherList({
        newEmails,
        schoolId: params.schoolId,
        missingEmails,
        schoolTeachers,
        pendingStaff,
        rejectedTeachers
      });
    } else {
      setErrorMessage('Confirm the conditions above.');
    }
  };

  return (
    <ConfirmActionModal handleClose={() => setShowModal(false)} size='m' hideCloseBtn>
      {({onCancel}) => (
        <>
          <Dialogue.Body align='center'>
            <div className='u-color_slate-800'>
              <Icon
                size='inherit'
                icon={['fal', 'exclamation-circle']}
                className='update-teachers__warning-icon u-mar-r_2 u-mar-b_4 u-color_black u-mar-lr_auto u-display_block u-color_slate-800'
              />
              <Text bold>
                Teachers will be removed from your staff list. This action cannot be undone.
              </Text>
            </div>
            <div className='update-teachers__confirm-checkbox'>
              <Checkbox
                checked={checked}
                onChange={onCheckboxChange}
                className='u-align-items_center'
              />
              <Text className='u-text-align_left u-color_slate-800'>
                I understand that{' '}
                <Text bold>
                  {missingEmails.length} {pluralize('teacher', missingEmails.length)} will be
                  removed
                </Text>{' '}
                from my staff list.
              </Text>
              {errorMessage && (
                <Text className='update-teachers__confirm-error u-text-align_left'>
                  {errorMessage}
                </Text>
              )}
            </div>
          </Dialogue.Body>
          <Dialogue.BtnGroup align='right' className='u-display_inline'>
            <Button color='secondary' onClick={onCancel}>
              Cancel
            </Button>
            <Button
              {...(checked && {
                to: `/school-admin/${params.schoolId}/manage-staff`
              })}
              as={RouterLink}
              onClick={onSubmit}
            >
              Continue
            </Button>
          </Dialogue.BtnGroup>
        </>
      )}
    </ConfirmActionModal>
  );
};

WarningModal.propTypes = {
  missingEmails: PropTypes.array,
  setShowModal: PropTypes.func,
  params: PropTypes.object,
  newEmails: PropTypes.array,
  schoolTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  rejectedTeachers: PropTypes.instanceOf(getModelForResourceType('school_v5')),
  pendingStaff: PropTypes.instanceOf(getModelForResourceType('school_v5'))
};

export default RefreshTeacherList;
